import React, { useEffect, useState } from 'react'

import { MotifButton, MotifIcon } from '@ey-xd/motif-react'
import { useMsal } from '@azure/msal-react'
import { loginRequest } from '../../services/security/authConfig'
import { useDispatch, useSelector } from 'react-redux'
import {
  IconoirArrowRight,
  IconoirArchery,
  IconoirSearchWindow,
} from '@ey-xd/motif-icon'
import { IUserState, setLoading } from '../../features/User/userSlice'
import Config from '@/configurations'
import Header from '@/components/header'
import PrivacyNoticeModal from '@/components/Modal/Privacy/PrivacyModal'
import styles from './index.module.scss'
import FreeTextDisclaimerModal from '@/components/Modal/Disclaimer/TextDisclaimer'
import { MotifProgressLoader } from '@ey-xd/motif-react'

// TODO: https://eysbp.visualstudio.com/CTP%20-%20Vancouver/_backlogs/backlog/FAIT%20SOD%20Beta%20Version/Epics/?workitem=6642707
export const Login: React.FC = () => {
  const dispatch = useDispatch()
  const { instance, accounts, inProgress } = useMsal()
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [showDisclaimer, setShowDisclaimer] = useState(false)
  const alreadyAgreed = localStorage.getItem('sod_agreed') === 'true'
  const [isProcessingRedirect, setIsProcessingRedirect] = useState(false)
  const isLoading = useSelector((state: IUserState) => state.isLoading)

  // Check if we're processing a redirect response
  useEffect(() => {
    const checkRedirectResponse = async () => {
      try {
        setIsProcessingRedirect(true)
        const response = await instance.handleRedirectPromise()
        
        if (response) {
          // User just completed login, keep loading state active
          console.log('Login successful, processing redirect...')
          // Don't set loading to false here - let the main app handle it
        } else {
          // No redirect response, safe to show login page
          setIsProcessingRedirect(false)
        }
      } catch (error) {
        console.error('Error processing redirect:', error)
        setIsProcessingRedirect(false)
        dispatch(setLoading(false))
      }
    }

    checkRedirectResponse()
  }, [instance, dispatch])

  // Hide login page if user is already authenticated
  if (accounts.length > 0 || isLoading || isProcessingRedirect || inProgress !== 'none') {
    return (
      <div className={styles.loadingWrapper}>
        <Header />
        <div className={styles.loadingContent}>
           <MotifProgressLoader
              aria-busy="true"
              aria-label="This page is loading"
              aria-live="polite"
              arialabel="This page is loading"
              role="alertdialog"
              show
              tabIndex={0}
              variant="default"
              fullscreen
            />
          <p>Completing authentication...</p>
        </div>
      </div>
    )
  }

  const handleLoginRedirect = () => {
    dispatch(setLoading(true))
    instance
      .loginRedirect({
        ...loginRequest,
        redirectUri: Config.RedirectUri,
      })
      .then((response) => {
        console.log('loginRedirect response: ', response)
      })
      .catch((error) => console.log('Login error', error))
  }

  return (
    <>
      <Header></Header>
      <div className={styles.loginWrapper}>
        <div className={styles.welcomeBlock}>
          <div className={styles.welcomeInnerBlock}>
            <div className={styles.welcomeInnerLeftBlock}>
              <div className={styles.welcomeInnerRightText}>Welcome</div>
              <div className={styles.welcomeInnerRightSupportingText}>
                Segregation of Duties (SOD)
              </div>
            </div>
            <MotifButton
              className={styles.loginButton}
              variant="primary-alt"
              title="login-alt"
              onClick={() => alreadyAgreed ? handleLoginRedirect() : setShowPrivacy(true)}
              size="medium"
              stepper="next"
              icons={{
                previous: null,
                next: IconoirArrowRight,
              }}
            >
              Get Started
            </MotifButton>
          </div>
        </div>

        <div className={styles.frame}>
          <div className={styles.frameOverview}>
            <div className={styles.frameHeadingAndText}>
              <div className={styles.frameHeading}>
                <MotifIcon
                  iconFunction={IconoirSearchWindow}
                  aria-hidden="true"
                  size="30"
                />
                <div className={styles.frameHeadingText}>Overview</div>
              </div>
              <div className={styles.frameSupportingText}>
                The Segregation of Duties (SOD) tool is designed to automate the segregation of duties analysis between developers and implementers. This tool enables engagement teams to upload various types of unstructured data—such as images, text, and PDFs—to conduct comparisons, helping to identify toxic combinations.
                <br />
                <br />
                Auditors will be prompted to navigate through the following modules:
                <ol>
                <li>Creation of Engagement Card</li>                
                <li>Developer/Implementer Access Input Files</li>
                <li>Evaluation of Appropriateness</li>
                <li>SOD Analysis</li>
                </ol>
                <b>Note:</b> The tool is currently not compatible with SAP applications. It is designed to support non-DevOps models through a traditional method of assessing developers in UAT environments or source code repository tools, and implementers in production environments. The tool is created agnostically to assess multiple environments, including OS systems (e.g., Windows, Unix), databases (e.g., Oracle, SQL), source code repositories (e.g., BitBucket, GitLab), and privileged access management tools (e.g., CyberArk).

              </div>
            </div>
          </div>
          <div className={styles.frameObjective}>
            <div className={styles.frameHeadingAndText}>
              <div className={styles.frameHeading}>
                <MotifIcon
                  iconFunction={IconoirArchery}
                  aria-hidden="true"
                  size="30"
                />
                <div className={styles.frameHeadingText}>Objective</div>
              </div>
              <div className={styles.frameSupportingText}>
                <ul>
                  <li>Streamline the auditing process with a simple, guided workflow.</li>
                  <li>Assist and educate auditors during the Segregation of Duties analysis.</li>
                  <li>Improve efficiency and reduce manual human error.</li>
                  <li>Generate a completed workbook for use as audit evidence, including all necessary file annotations.</li>
                </ul>
                <a href='https://live.atlas.ey.com' target="_blank" rel="noopener noreferrer">Click here for more information</a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PrivacyNoticeModal
        open={showPrivacy}
        onAgree={() => {
          setShowPrivacy(false)
          setShowDisclaimer(true)
        }}
      />
      <FreeTextDisclaimerModal
        open={showDisclaimer}
        onAgree={() => {
          setShowDisclaimer(false)
          localStorage.setItem('sod_agreed', 'true')
          handleLoginRedirect()
        }}
      />
    </>
  )
}
